import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, SpinLoading, Picker, SearchBar, Skeleton } from 'antd-mobile';
import { DownOutline } from 'antd-mobile-icons';
import { useQuery } from '@apollo/client';
import { getNavBarContainerStyle } from '../../../../utils/deviceUtils';
import { t } from '../../../../i18n';
import { DspPath } from '../../../../enums/DeliveryNavigatePathEnum';
import {DspAreasData, GET_DSP_AREAS, GetDspAreasResponse} from '../../graphql/queries/getDspWarehousesAndAreas';
import {DspWhsAndAreaDataType, GET_ORDER_DATA_OF_CURRENT_DSP_ALL_WAREHOUSES_AND_ALL_AREAS, GetOrderDataOfCurrentDspAllWarehousesAndAllAreasResponse} from '../../graphql/queries/getOrderDataOfCurrentDspAllWarehousesAndAllAreas';
import { getExpandableText } from '../../../../utils/textUtils';
import useDspWarehouseStore from '../../store/dspWarehouseStore';

/**
 * Receiving Sorting - 车队分组取包明细页面
 */
const ReceivingSortingDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const warehouseIdVar = selectedWarehouseId ? parseInt(selectedWarehouseId) : undefined;

    // 1. 获取 DSP 关联的区域
    const {
        data: areaData,
        loading: whsAreaLoading
    } = useQuery<GetDspAreasResponse>(GET_DSP_AREAS, {
        variables: { warehouseId: warehouseIdVar },
    });

    const AreasData: DspAreasData | undefined = areaData?.getDspAreas;



    // 2. 当前选中的区域（默认选择"请选择"，id=-1）
    const [selectedAreaId, setSelectedAreaId] = useState<number>(-1);

    // 3. Tab & 搜索 & 展开行
    const [activeTab, setActiveTab] = useState<'scanned' | 'unscanned'>('scanned');
    const [searchValue, setSearchValue] = useState<string>('');
    const [pickerAreaVisible, setPickerAreaVisible] = useState(false);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // 4. 初始化默认的区域为"请选择"，id为-1
    React.useEffect(() => {
        if (!AreasData?.areas || AreasData.areas.length === 0) return;
        // 不再默认选择第一个区域，而是保持初始状态为"请选择"
    }, [AreasData]);

    // 6. 根据选中的区域获取运单数据
    const {
        data: orderData,
        loading: orderLoading,
        error: orderError,
        refetch: refetchOrderData
    } = useQuery<GetOrderDataOfCurrentDspAllWarehousesAndAllAreasResponse>(
        GET_ORDER_DATA_OF_CURRENT_DSP_ALL_WAREHOUSES_AND_ALL_AREAS,
        {
            skip: selectedAreaId === -1,
            variables:
                selectedAreaId !== -1
                    ? { areaId: selectedAreaId, warehouseId: warehouseIdVar }
                    : undefined,
            fetchPolicy: 'network-only'
        }
    );

    // 当区域变化时，useQuery的variables变化会自动重新查询数据（不重新加载页面）
    // 使用refetch可以在Picker选择时手动触发查询，确保数据及时更新

    const dspOrderData = orderData?.getOrderDataOfCurrentDspAllWarehousesAndAllAreas;

    // 7. 统计数据：线路 & 单量 & 包裹扫描情况
    const {
        assignedOrderCount,
        scannedParcelCount,
        unscannedParcelCount,
        parcelList
    } = useMemo(() => {
        const parcels: DspWhsAndAreaDataType[] = dspOrderData?.parcelList || [];
        const scannedCount = parcels.filter(p => p.isPickedUp).length;
        const unscannedCount = parcels.length - scannedCount;
        return {
            assignedOrderCount: dspOrderData?.assignedOrderCount ?? 0,
            scannedParcelCount: scannedCount,
            unscannedParcelCount: unscannedCount,
            parcelList: parcels
        };
    }, [dspOrderData]);

    // 8. 当前 Tab 下展示的包裹列表（支持按 trackingNumber 搜索）
    const displayParcels = useMemo(() => {
        let filtered = parcelList;

        // 按 Tab 过滤：已扫描 = isPickedUp，未扫描 = !isPickedUp
        filtered =
            activeTab === 'scanned'
                ? filtered.filter(p => p.isPickedUp)
                : filtered.filter(p => !p.isPickedUp);

        if (searchValue.trim()) {
            const keyword = searchValue.trim().toLowerCase();
            filtered = filtered.filter(p => p.trackingNumber.toLowerCase().includes(keyword));
        }

        return filtered;
    }, [parcelList, activeTab, searchValue]);

    const handleBack = () => {
        navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN);
    };

    const toggleRowExpanded = (trackingNumber: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(trackingNumber)) {
                next.delete(trackingNumber);
            } else {
                next.add(trackingNumber);
            }
            return next;
        });
    };

    // 不再显示整个页面的loading，改为在包裹列表区域显示Skeleton

    const currentAreaName =
        selectedAreaId === -1 
            ? t('pages.dsp.home.receivingSorting.detailPage.pleaseSelect') 
            : AreasData?.areas.find(a => a.id === selectedAreaId)?.name || '-';

    const areaPickerColumns = useMemo(() => {
        if (!AreasData?.areas) return [[]];
        return [
            AreasData.areas.map(area => ({
                label: area.name,
                value: area.id as number
            }))
        ];
    }, [AreasData?.areas]);

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* Navbar */}
            <div {...getNavBarContainerStyle()} className="bg-white">
                <NavBar onBack={handleBack}>
                    <span className="font-bold">{t('pages.dsp.home.receivingSorting.detailPage.dspScanDetail')}</span>
                </NavBar>
            </div>

            {/* 统计信息 + 区域选择 */}
            <div className="bg-white px-4 py-3">
                {/* 区域选择 和 车队已扫描线路 */}
                <div className="flex items-center justify-between mb-3">
                    {/* 车队已扫描线路 */}
                    <div className="text-sm text-gray-600 font-bold">
                        {t('pages.dsp.home.receivingSorting.detailPage.dspTotalOrder')}:{assignedOrderCount}
                    </div>
                    {/* 区域选择 */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">{t('pages.dsp.home.receivingSorting.detailPage.area')}</span>
                        <div
                            className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded bg-white active:bg-gray-50 cursor-pointer"
                            onClick={() => setPickerAreaVisible(true)}
                        >
                            <span className="text-sm text-gray-700">{currentAreaName}</span>
                            <DownOutline fontSize={12} className="text-gray-400" />
                        </div>
                    </div>
                </div>
                {/* 统计卡片：左=已扫描，右=未扫描 */}
                <div className="flex gap-4">
                    <div className="flex-1 bg-purple-50 rounded-lg p-4 text-center">
                        <div className="text-sm text-black mb-1 font-bold">{t('pages.dsp.home.receivingSorting.detailPage.scanned')}</div>
                        <div className="text-2xl font-semibold text-gray-600">{scannedParcelCount}</div>
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-sm text-black mb-1 font-bold">{t('pages.dsp.home.receivingSorting.detailPage.unscanned')}</div>
                        <div className="text-2xl font-semibold text-gray-600">{unscannedParcelCount}</div>
                    </div>
                </div>
            </div>

            {/* 包裹扫描明细 */}
            <div className="bg-white px-4 py-3 mt-2 border-b border-gray-200">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-base font-medium">
                        {t('pages.dsp.home.receivingSorting.detailPage.parcelScanDetail')}
                    </span>
                </div>
                {/* 搜索框 */}
                <div className="py-2 border-b border-gray-200">
                    <SearchBar
                        placeholder={t('pages.dsp.home.receivingSorting.detailPage.searchPlaceholder')}
                        value={searchValue}
                        onChange={setSearchValue}
                    />
                </div>

                {/* Tab 切换：已扫描 / 未扫描 */}
                <div className="flex border-b border-gray-200">
                    <button
                        className={`flex-1 pb-2 text-sm font-medium transition-colors ${
                            activeTab === 'scanned'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setActiveTab('scanned')}
                    >
                        {t('pages.dsp.home.receivingSorting.detailPage.scanned')}
                    </button>
                    <button
                        className={`flex-1 pb-2 text-sm font-medium transition-colors ${
                            activeTab === 'unscanned'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500'
                        }`}
                        onClick={() => setActiveTab('unscanned')}
                    >
                        {t('pages.dsp.home.receivingSorting.detailPage.unscanned')}
                    </button>
                </div>
            </div>

            {/* 表头 */}
            <div className="p-1 border-b border-gray-200">
                <div className="grid grid-cols-12 mb-1 font-medium text-gray-500 gap-1">
                    <div className="col-span-7 px-1 text-center break-words">
                        {t('pages.dsp.home.receivingSorting.detailPage.trackingNumber')}
                    </div>
                    <div className="col-span-2 px-1 text-center break-words">
                        {t('pages.dsp.home.receivingSorting.detailPage.stop')}
                    </div>
                    <div className="col-span-3 px-1 text-center break-words">
                        {activeTab === 'scanned'
                            ? t('pages.dsp.home.receivingSorting.detailPage.lastScanner')
                            : t('pages.dsp.home.receivingSorting.detailPage.assignedDriver')}
                    </div>
                </div>
            </div>

            {/* 包裹列表 */}
            <div className="flex-1 overflow-y-auto bg-white relative">
                {(orderLoading || whsAreaLoading) ? (
                    <div className="p-4">
                        {Array.from({length: 8}).map((_, index) => (
                            <div key={index} className="grid grid-cols-12 py-3 border-b border-gray-100">
                                <div className="col-span-7 px-1 flex items-center justify-center">
                                    <Skeleton.Title style={{width: '90%'}} animated/>
                                </div>
                                <div className="col-span-2 px-1 flex items-center justify-center">
                                    <Skeleton.Title style={{width: '60%'}} animated/>
                                </div>
                                <div className="col-span-3 px-1 flex items-center justify-center">
                                    <Skeleton.Title style={{width: '70%'}} animated/>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : displayParcels.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-gray-400 text-sm">
                            {t('pages.dsp.home.receivingSorting.detailPage.noData')}
                        </div>
                    </div>
                ) : (
                    [...displayParcels].sort((a, b) => {
                        // 首先比较 stopNumber
                        const stopNumberA = a.stopNumber;
                        const stopNumberB = b.stopNumber;
                        
                        if (stopNumberA !== stopNumberB) {
                            return stopNumberA - stopNumberB;
                        }
                        
                        // 如果 stopNumber 相同，则比较 subStopNumber
                        // 处理 subStopNumber 为 null 或 undefined 的情况
                        const subStopA = a.subStopNumber ? parseInt(a.subStopNumber, 10) : 0;
                        const subStopB = b.subStopNumber ? parseInt(b.subStopNumber, 10) : 0;
                        
                        return subStopA - subStopB;
                    }).map((parcel, index) => {
                        const stopDisplay = parcel.subStopNumber
                            ? `${parcel.stopNumber}-${parcel.subStopNumber}`
                            : `${parcel.stopNumber}`;

                        const scannerName = activeTab === 'scanned' 
                          ? (parcel.lastScannerName || '-') 
                          : (parcel.allocationDriverName || '-');
                        const isExpanded = expandedRows.has(parcel.trackingNumber);
                        const { displayText, needsTruncate } = getExpandableText(scannerName, isExpanded, 7);

                        return (
                            <div
                                key={`${parcel.trackingNumber}-${index}`}
                                className="grid grid-cols-12 py-3 border-b border-gray-100 gap-1"
                            >
                                <div className="col-span-7 px-1 font-mono text-sm text-center whitespace-nowrap overflow-x-auto">
                                    {parcel.trackingNumber}
                                </div>
                                <div className="col-span-2 px-1 font-mono text-sm text-center break-words">
                                    {stopDisplay}
                                </div>
                                <div
                                    className={`col-span-3 px-1 font-mono text-sm text-center break-words ${
                                        needsTruncate ? 'cursor-pointer active:bg-gray-100' : ''
                                    }`}
                                    onClick={() =>
                                        needsTruncate && toggleRowExpanded(parcel.trackingNumber)
                                    }
                                >
                                    {displayText}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* 区域选择 Picker */}
            <Picker
                columns={areaPickerColumns}
                visible={pickerAreaVisible}
                onClose={() => setPickerAreaVisible(false)}
                onConfirm={async value => {
                    const areaId = value[0] as number;
                    setSelectedAreaId(areaId);
                    setPickerAreaVisible(false);
                    // 手动触发查询
                    await refetchOrderData({
                        areaId: areaId,
                        warehouseId: warehouseIdVar
                    });
                }}
                value={selectedAreaId !== -1 ? [selectedAreaId] : []}
                title={`${t('pages.dsp.home.receivingSorting.detailPage.selectArea')}`}
            />
        </div>
    );
};

export default ReceivingSortingDetailsPage;